import classnames from "classnames";
import { differenceInDays, differenceInMonths, format } from "date-fns";
import { ReactNode } from "react";
import { Cell, Label, Pie, PieChart } from "recharts";
import { CurrencyFormat } from "../CurrencyFormat";
import { SvgInline } from "../SvgInline/SvgInline";
import styles from "./plan-card.module.scss";

type FieldProps = {
  label: string;
  value: ReactNode;
  className?: string; // TODO: passing a className as a prop is an anti-pattern
};

const Field = ({ label, value, className }: FieldProps) => (
  <div className={styles.field}>
    <span className={styles.label}>{label}</span>
    <span className={`${styles.value} ${className}`}>{value}</span>
  </div>
);

const formatDate = (value: string): string =>
  format(new Date(value), "dd MMM yyyy");

type Props = {
  loanAmount: number;
  loanBalance: number;
  currentLoanTerm: number;
  currentMaturityDate: string;
  openedOnDate: string;
  store: string;
};

export const PlanCard = ({
  loanBalance,
  openedOnDate,
  currentMaturityDate,
  currentLoanTerm,
  loanAmount,
  store,
}: Props) => {
  let termRemaining = differenceInMonths(
    new Date(currentMaturityDate),
    new Date(),
  );
  termRemaining = termRemaining > 0 ? termRemaining : 0;

  let termRemainingClass = styles.remaining;
  termRemainingClass =
    loanBalance > 0 && termRemaining === 0
      ? termRemainingClass
      : `${termRemainingClass} text-danger`;

  const paid = loanAmount - loanBalance;
  const colors = {
    brandBlueBright: "#0046aa",
    brandGreyLight: "#e5e5e5",
    lfsDarkBlue: "#006aff",
    lfsRed: "#cf000f",
    brandLightGreen: "#21a637",
    brandGreenDark: "#1b633c",
  };

  const pieData = [
    {
      value: paid,
      color: "brandBlueBright",
    },
    {
      value: loanBalance,
      color: "brandGreyLight",
    },
  ];

  const data02 = new Array(currentLoanTerm).fill({ value: 1 });

  const getFillColour = (index) => {
    if (currentLoanTerm - index > termRemaining) {
      return colors.lfsDarkBlue;
    }
    return colors.brandGreyLight;
  };

  const planSummaryClass = classnames(styles.summary, {
    [styles.expired]: termRemaining <= 0,
  });

  return (
    <div className={planSummaryClass} data-testid={`plan-card-${store}`}>
      {store && <div className={`${styles.title} subtitle02`}>{store}</div>}
      <div className={styles.primaryDetail}>
        <div className={styles.primaryText}>
          <div className={`subtitle01 ${styles.balance}`}>
            <CurrencyFormat value={loanBalance} />
          </div>
          <div>
            remaining of <CurrencyFormat value={loanAmount} />
          </div>
          <hr className={styles.hrdotted} />
          {termRemaining >= 3 ? (
            <div className="body03">
              <span className={termRemainingClass}>
                {termRemaining} of {currentLoanTerm}
              </span>{" "}
              months remaining
            </div>
          ) : (
            <div className="body03">
              <span className={termRemainingClass}>
                {differenceInDays(new Date(currentMaturityDate), new Date())}
              </span>{" "}
              days remaining
            </div>
          )}
        </div>
        <div>
          <PieChart width={100} height={100}>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={32}
              outerRadius={39}
              startAngle={90}
              endAngle={-270}
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  stroke={loanBalance <= 0 ? colors.brandGreenDark : ""}
                  fill={
                    loanBalance <= 0
                      ? colors.brandGreenDark
                      : colors[entry.color]
                  }
                />
              ))}
              {loanBalance <= 0 && (
                <Label
                  position="center"
                  content={<SvgInline name="icon-tick-green" />}
                />
              )}
            </Pie>
            <Pie
              data={data02}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={43}
              outerRadius={50}
              startAngle={90}
              endAngle={-270}
            >
              {data02.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    loanBalance <= 0
                      ? colors.brandLightGreen
                      : getFillColour(index)
                  }
                />
              ))}
            </Pie>
          </PieChart>
        </div>
      </div>

      <div className={styles.secondaryDetail}>
        <Field
          label="Original purchase(s)"
          value={<CurrencyFormat value={loanAmount} />}
        />

        <Field
          label="Total paid"
          value={<CurrencyFormat value={paid} />}
          className={
            loanBalance <= 0
              ? "text-color-brand-dark-green"
              : "text-color-brand"
          }
        />

        <Field label="Start date" value={formatDate(openedOnDate)} />

        <Field label="Expiry date" value={formatDate(currentMaturityDate)} />

        <Field
          label="Interest free period"
          value={
            currentLoanTerm === 1 ? "1 month" : `${currentLoanTerm} months`
          }
        />
      </div>
    </div>
  );
};
